/* Used for push notifications, cookies and popups. */
import React, { useRef, useState, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import Button from '../Elements/Button/Button';
import Newsletter from '../Footer/Newsletter';
import VideoPlayer from '../../hocs/HLSVideo/HLSVideo';
import { addClass, removeClass, purifier, existsCookie, getCookie, setCookie, height, addEvent, removeEvent } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { translate } from '../../utils/locale';
import { isRestricted } from '../../utils/restrict';
import { urlify } from '../../utils/string';
import s from './Notification.scss';

const Notification = ({ type, content = [], setup }) => {
	const notificationInner = useRef(null);
	const [elementRemoved, removingElement] = useState(false);
	const [isExiting, toggleExiting] = useState(false);
	const [notificationIndex, incrementNotificationIndex] = useState(0);

	const notifications = content.filter((n) => {
		const notificationName = urlify(n.name);
		if (n.countryRestrict && isRestricted(setup, { selectedCountries: n.countryRestrict })) return false;
		// Check for cookies which are already set.
		if ((n.removable || type === 'popup') && n.cookieDuration) {
			let cookieIsSet = false;

			const cookies = typeof document !== 'undefined' ? getCookie(notificationName) : setup.cookies;
			if (cookies && typeof cookies === 'object') {
				if (cookies[notificationName]) cookieIsSet = true;
			} else if (cookies) {
				cookieIsSet = true;
			}

			if (type === 'popup' && typeof document !== 'undefined' && existsCookie('emailHash')) return false;

			// Still render notification if from server and cookie not set on page load.
			// This enables us to fade it out.
			if (cookieIsSet || elementRemoved) return null;
		}
		if (n.content) {
			const hasContent = n.content.filter((c) => {
				if (c.countryRestrictContent && isRestricted(setup, { selectedCountries: c.countryRestrictContent })) return false;
				if (!c.text && !c.icon) return false;
				return true;
			});
			if (!hasContent || hasContent.length === 0) return false;
		}
		if (!n.content && !n.text) return false;
		return true;
	});

	let campaignCycle = null;

	const [delay, updateDelay] = useState(notifications.reduce((acc, n) => (n.delay ? { ...acc, [urlify(n.name)]: false } : acc), {}));

	useEffect(() => {
		let clearShowingElementTimeout;
		if (isExiting) {
			if (type === 'campaign') removeClass(document.querySelector('html'), 'u_campaignNotification');
			// Remove element
			clearShowingElementTimeout = window.setTimeout(() => {
				removingElement(true);
				toggleExiting(false);
			}, 2000);
		}

		return () => {
			window.clearTimeout(clearShowingElementTimeout);
		};
	}, [isExiting]);

	useEffect(() => {
		let delayTimer = null;

		if (setup.cookieConsentSet) {
			notifications.forEach((n) => {
				if (n.delay) {
					const [timer = false] = n.delay.filter((d) => /^\d+$/.test(d));
					const [scroller = false] = n.delay.filter((d) => /^scroll\d+$/.test(d));
					if (timer) {
						delayTimer = window.setTimeout(() => {
							window.clearTimeout(delayTimer);
							removeEvent(window, 'scroll.popup');
							updateDelay((prevState) => ({
								...prevState.delay,
								[urlify(n.name)]: true,
							}));
						}, parseInt(timer, 10) * 1e3);
					}
					if (scroller) {
						const documentHeight = height(document) * (parseInt(scroller.replace('scroll', ''), 10) / 100);

						addEvent(window, 'scroll.popup', () => {
							window.requestAnimationFrame(() => {
								const top = window.pageYOffset || document.documentElement.scrollTop;
								if (top + window.innerHeight > documentHeight) {
									window.clearTimeout(delayTimer);
									removeEvent(window, 'scroll.popup');
									updateDelay((prevState) => ({
										...prevState.delay,
										[urlify(n.name)]: true,
									}));
								}
							});
						});
					}
				}
			});
		}
	}, [setup.cookieConsentSet]);

	useEffect(() => {
		const started = window.performance.now();

		const initiateSiteListeners = () => {
			if (!notifications && started + 2000 < window.performance.now()) {
				window.setTimeout(initiateSiteListeners, 200);
			}

			if (notifications && notifications.length > 0 && type === 'campaign') addClass(document.querySelector('html'), 'u_campaignNotification');

			if (notifications && notifications.length > 1 && type === 'campaign') {
				campaignCycle = window.setInterval(() => {
					incrementNotificationIndex((prevState) => (prevState.notificationIndex + 1));
				}, 4000);
			}
		};

		initiateSiteListeners();

		return () => {
			clearInterval(campaignCycle);
			removeClass(document.querySelector('html'), 'u_campaignNotification');
		};
	}, []);

	if (notifications.length === 0) return null;

	return (
		<div className={[s.notification, s[`notification__${type}`]].join(' ')}>
			{
				notifications.map((item, i) => {
					const notificationName = urlify(item.name);
					const notificationAttributes = {
						style: {},
					};
					const notificationInnerClasses = [s.inner, (item.removeText ? [s.inner__closeText] : null)];

					if (isExiting) notificationInnerClasses.push(s.inner__exiting);
					if (item.backgroundColor) notificationAttributes.style.backgroundColor = item.backgroundColor;
					if (item.textColor) notificationAttributes.style.color = item.textColor;
					if (notificationIndex % notifications.length === i) notificationInnerClasses.push(s.inner__selected);

					// Item delayed don't show yet.
					if (item.delay && delay[notificationName] === false) return null;

					const innerTextContent = translate(setup, item?.text).replace('%country%', setup.countries[setup.country].name);

					const innerContent = item.content && item.content.filter((c) => {
						if (c.countryRestrictContent && isRestricted(setup, { selectedCountries: c.countryRestrictContent })) return false;
						if (!c.text && !c.icon) return false;
						return true;
					}).reduce((acc, c) => {
						if (c?.static) acc.static = [...(acc?.static || []), c];
						else acc.scrolling = [...(acc?.scrolling || []), c];
						return acc;
					}, { static: [], scrolling: [] });

					const removeNotificationHandler = (e) => {
						if (e) e.preventDefault();
						setCookie(notificationName, 1, item.cookieDuration);
						toggleExiting(true);
					};

					const newsletterSubmitHandler = () => {
						setTimeout(() => {
							removeNotificationHandler();
						}, 500);
					};

					return (
						<>
							{ type === 'popup' && <button className={[s.notification_screen, isExiting ? s.notification_screen__exiting : null].join(' ')} type="button" onClick={removeNotificationHandler} aria-label="Close" /> }
							<div ref={notificationInner} className={notificationInnerClasses.join(' ')} {...notificationAttributes}>
								{
									((innerContent?.static && innerContent.static.length > 0) || (innerContent?.scrolling && innerContent.scrolling.length > 0)) && (
										<div className={[s.inner_items, (item.removable || type === 'popup') && item.cookieDuration ? s.inner_items__closeable : null, innerContent.scrolling.length === 0 && innerContent.static.length === 1 ? s.inner_items__singular : null].join(' ')}>
											{
												innerContent.scrolling.length > 0 && (
													<div className={s.inner_items_scrolling}>
														<div className={s.inner_items_scrolling_inner}>
															{
																[...innerContent.scrolling, ...innerContent.scrolling, ...innerContent.scrolling].map((c) => {
																	const tempInnerText = translate(setup, c.text);
																	const innerContentReturn = [];
																	if (c.icon) innerContentReturn.push(<img className={s.inner_item_icon} src={c.icon.url} alt={c.icon.alt} />);
																	else if (tempInnerText) innerContentReturn.push(<span className={s.inner_item_text} dangerouslySetInnerHTML={{ __html: purifier(tempInnerText.replace('%country%', setup.countries[setup.country].name)) }} />);
																	return <div className={s.inner_item}>{ innerContentReturn }</div>;
																})
															}
														</div>
													</div>
												)
											}
											{
												innerContent.static.length > 0 && (
													<div className={s.inner_items_static}>
														{
															innerContent.static.map((c) => {
																const tempInnerText = translate(setup, c.text);
																const innerContentReturn = [];
																if (c.icon) innerContentReturn.push(<img className={s.inner_item_icon} src={c.icon.url} alt={c.icon.alt} />);
																else if (tempInnerText) innerContentReturn.push(<span className={s.inner_item_text} dangerouslySetInnerHTML={{ __html: purifier(tempInnerText.replace('%country%', setup.countries[setup.country].name)) }} />);
																return <div className={s.inner_item}>{ innerContentReturn }</div>;
															})
														}
													</div>
												)
											}
										</div>
									)
								}
								{
									(item?.image?.mobile || item?.image?.desktop) && !item?.film?.mobile && !item?.film?.desktop && (
										<div className={s.inner_side}>
											<picture className={s.inner_side_media}>
												{ item?.image?.mobile && <source {...imageSizeTranslation('4', item.image.mobile, '-', false)} media="only screen and (max-width: 600px)" /> }
												{ item?.image?.desktop && <img {...imageSizeTranslation('4', item.image.desktop, '-', false)} /> }
											</picture>
										</div>
									)
								}
								{
									(item?.film?.mobile || item?.film?.desktop) && (
										<div className={s.inner_side}>
											{ setup.breakPoint === 'mobile' && item?.film?.mobile && <VideoPlayer className={s.inner_side_media} src={item.film.mobile.url} autoPlay loop muted playsInline /> }
											{ setup.breakPoint !== 'mobile' && item?.film?.desktop && <VideoPlayer className={s.inner_side_media} src={item.film.desktop.url} autoPlay loop muted playsInline /> }
										</div>
									)
								}
								{
									(innerTextContent || item.includeNewsletter) && (
										<div className={s.inner_side}>
											{ innerTextContent && <div className={s.inner_side_text} dangerouslySetInnerHTML={{ __html: purifier(innerTextContent) }} /> }
											{ item.includeNewsletter && <Newsletter callback={newsletterSubmitHandler} variant="popup" /> }
										</div>
									)
								}
								{
									(item.removable || type === 'popup') && item.cookieDuration && (
										<div className={s.inner_close} style={{ backgroundColor: notificationAttributes?.style?.backgroundColor || 'transparent' }}>
											<Button type="button" clickEventHandler={removeNotificationHandler} variantClasses={item.removeText ? [] : ['button__invisible']}>
												{
													item.removeText && translate(setup, item.removeText)
												}
												{
													!item.removeText && (
														<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path d="M13 1L1 13" />
															<path d="M1 1L13 13" />
														</svg>
													)
												}
											</Button>
										</div>
									)
								}
							</div>
						</>
					);
				})
			}
		</div>
	);
};

export default hot(module)(Notification);
